import { useState } from "react";
import { Building2, ExternalLink, FileText, GraduationCap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Linkedin } from "@/components/icons/linkedin";
import { AIBadge, PersonAvatar, RelativeTime } from "@/components/data";
import { toneSoftClasses } from "@/lib/tones";
import { cn } from "@/lib/utils";
import type { LinkedInExtracted } from "@/types/linkedin";
import {
  chaveExperiencia,
  chaveFormacao,
  chaveSkill,
  periodoDaExperiencia,
  type CampoDoPerfil,
  type MarcaDaLeitura,
  type MarcasDaLeitura,
} from "./linkedinPerfil";

const MARCA_TONE = { novo: "success", mudou: "warning" } as const;

function Marca({ marca }: { marca: MarcaDaLeitura | null | undefined }) {
  if (!marca) return null;
  return (
    <Badge tone={MARCA_TONE[marca]} className="shrink-0 px-1.5 py-0 text-[10px]" data-testid={`linkedin-marca-${marca}`}>
      {marca}
    </Badge>
  );
}

const SKILLS_VISIVEIS = 6;

/** Cada seção é um cartão próprio, como no LinkedIn. */
function Bloco({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <h3 className="text-base font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function Capa({ capaUrl }: { capaUrl?: string | null }) {
  const [falhou, setFalhou] = useState(false);
  if (capaUrl && !falhou) {
    return <img src={capaUrl} alt="" className="h-24 w-full object-cover" onError={() => setFalhou(true)} data-testid="linkedin-simulado-capa" />;
  }
  return <div className={cn("h-24", toneSoftClasses.accent)} aria-hidden />;
}

function Competencias({ skills, marcas }: { skills: LinkedInExtracted["skills"]; marcas: MarcasDaLeitura | null }) {
  const [todas, setTodas] = useState(false);
  const visiveis = todas ? skills : skills.slice(0, SKILLS_VISIVEIS);
  return (
    <>
      <ul className="divide-y">
        {visiveis.map((skill) => (
          <li key={skill.nome} className="flex items-center justify-between gap-2 py-2 first:pt-0 last:pb-0">
            <span className="text-sm font-semibold">{skill.nome}</span>
            <Marca marca={marcas?.skills.has(chaveSkill(skill.nome)) ? "novo" : null} />
          </li>
        ))}
      </ul>
      {skills.length > SKILLS_VISIVEIS && (
        <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setTodas((current) => !current)}>
          {todas ? "Mostrar menos" : `Mostrar todas as ${skills.length} competências`}
        </Button>
      )}
    </>
  );
}

function Sobre({ texto, marca }: { texto: string; marca: MarcaDaLeitura | null }) {
  const [aberto, setAberto] = useState(false);
  const longo = texto.length > 320;
  return (
    <div className="space-y-1">
      <div className="flex items-start gap-2">
        <p className={cn("whitespace-pre-line text-sm", longo && !aberto && "line-clamp-5")}>{texto}</p>
        <Marca marca={marca} />
      </div>
      {longo && (
        <Button variant="link" className="h-auto px-0 text-xs" onClick={() => setAberto((current) => !current)}>
          {aberto ? "ver menos" : "ver mais"}
        </Button>
      )}
    </div>
  );
}

interface LinkedInPerfilSimuladoProps {
  perfil: LinkedInExtracted;
  /** Null quando não há leitura anterior para comparar. */
  marcas: MarcasDaLeitura | null;
  url: string | null;
  pdfUrl?: string | null;
  lidoEm?: string | null;
  /** O que esta leitura é: "Leitura em revisão" ou "Perfil aplicado". */
  legenda: string;
  /** Leitura em revisão sem nenhuma anterior: avisa que não há marcas. */
  primeiraLeitura?: boolean;
  /** Foto e capa copiadas para o S3; sem elas, as iniciais e a faixa lisa. */
  fotoUrl?: string | null;
  capaUrl?: string | null;
}

/**
 * O perfil do LinkedIn como a IA leu, na ordem em que o LinkedIn mostra: topo,
 * Sobre, Experiência, Formação, Competências e Idiomas. O que mudou desde a
 * última leitura aplicada vem marcado. O nome abre o perfil de verdade.
 */
export function LinkedInPerfilSimulado({ perfil, marcas, url, pdfUrl, lidoEm, legenda, primeiraLeitura, fotoUrl, capaUrl }: LinkedInPerfilSimuladoProps) {
  const marcaCampo = (campo: CampoDoPerfil): MarcaDaLeitura | null => (marcas?.campos.has(campo) ? "mudou" : null);
  const novas = marcas ? perfil.skills.filter((skill) => marcas.skills.has(chaveSkill(skill.nome))).length : 0;
  const empresaAtual = perfil.experiencias.find((experiencia) => experiencia.is_atual)?.empresa_nome ?? null;
  const formacaoRecente = perfil.formacao[0]?.instituicao ?? null;

  return (
    <div className="space-y-2" data-testid="linkedin-simulado">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <AIBadge />
        <span>{legenda}</span>
        {lidoEm && (
          <span>
            · lido <RelativeTime value={lidoEm} />
          </span>
        )}
        {primeiraLeitura && <span>· primeira leitura, sem comparação</span>}
      </div>

      <article className="overflow-hidden rounded-lg border bg-card">
        <Capa capaUrl={capaUrl} />
        <div className="space-y-3 px-4 pb-4">
          <PersonAvatar name={perfil.nome_completo} src={fotoUrl} size="lg" className="-mt-12 h-24 w-24 text-2xl ring-4 ring-card" />
          <div className="space-y-1">
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-1.5 text-xl font-semibold hover:underline"
                data-testid="linkedin-simulado-nome"
              >
                {perfil.nome_completo}
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </a>
            ) : (
              <p className="text-xl font-semibold" data-testid="linkedin-simulado-nome">
                {perfil.nome_completo}
              </p>
            )}
            {perfil.headline && (
              <div className="flex items-start gap-2">
                <p className="text-sm leading-snug">{perfil.headline}</p>
                <Marca marca={marcaCampo("headline")} />
              </div>
            )}
            {perfil.localizacao && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{perfil.localizacao}</span>
                <Marca marca={marcaCampo("localizacao")} />
              </div>
            )}
            {(empresaAtual || formacaoRecente) && (
              <div className="space-y-0.5 pt-1 text-xs font-medium">
                {empresaAtual && (
                  <p className="flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    {empresaAtual}
                  </p>
                )}
                {formacaoRecente && (
                  <p className="flex items-center gap-1.5">
                    <GraduationCap className="h-3.5 w-3.5 text-muted-foreground" />
                    {formacaoRecente}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {url ? (
              <Button asChild size="sm" className="h-8 rounded-full text-xs">
                <a href={url} target="_blank" rel="noopener noreferrer" data-testid="linkedin-simulado-abrir">
                  <Linkedin className="h-3.5 w-3.5" />
                  Abrir no LinkedIn
                </a>
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" disabled title="A leitura não trouxe a URL do perfil">
                <Linkedin className="h-3.5 w-3.5" />
                Sem URL nesta leitura
              </Button>
            )}
            {pdfUrl && (
              <Button asChild size="sm" variant="outline" className="h-8 rounded-full text-xs">
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                  <FileText className="h-3.5 w-3.5" />
                  Ver PDF
                </a>
              </Button>
            )}
          </div>
        </div>
      </article>

      {perfil.resumo && (
        <Bloco title="Sobre">
          <Sobre texto={perfil.resumo} marca={marcaCampo("resumo")} />
        </Bloco>
      )}

      {perfil.experiencias.length > 0 && (
        <Bloco title="Experiência">
          <ul className="divide-y">
            {perfil.experiencias.map((experiencia, index) => {
              const periodo = periodoDaExperiencia(experiencia.data_inicio, experiencia.data_fim, experiencia.is_atual);
              return (
                <li key={`${chaveExperiencia(experiencia)}-${index}`} className="flex gap-3 py-3 first:pt-0 last:pb-0" data-testid="linkedin-simulado-experiencia">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-muted text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold">{experiencia.cargo_nome}</p>
                      <Marca marca={marcas?.experiencias.get(chaveExperiencia(experiencia))} />
                    </div>
                    <p className="text-sm">{experiencia.empresa_nome}</p>
                    {periodo && <p className="text-xs text-muted-foreground">{periodo}</p>}
                    {experiencia.localizacao && <p className="text-xs text-muted-foreground">{experiencia.localizacao}</p>}
                    {experiencia.descricao && (
                      <p className="line-clamp-3 whitespace-pre-line pt-1 text-xs text-muted-foreground">{experiencia.descricao}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Bloco>
      )}

      {perfil.formacao.length > 0 && (
        <Bloco title="Formação">
          <ul className="space-y-3">
            {perfil.formacao.map((formacao, index) => (
              <li key={`${chaveFormacao(formacao)}-${index}`} className="flex gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-muted text-muted-foreground">
                  <GraduationCap className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold">{formacao.instituicao}</p>
                    <Marca marca={marcas?.formacoes.has(chaveFormacao(formacao)) ? "novo" : null} />
                  </div>
                  {formacao.curso && <p className="text-sm">{formacao.curso}</p>}
                  {formacao.periodo && <p className="text-xs text-muted-foreground">{formacao.periodo}</p>}
                </div>
              </li>
            ))}
          </ul>
        </Bloco>
      )}

      {perfil.skills.length > 0 && (
        <Bloco title={novas > 0 ? `Competências (${novas} novas)` : "Competências"}>
          <Competencias skills={perfil.skills} marcas={marcas} />
        </Bloco>
      )}

      {perfil.idiomas.length > 0 && (
        <Bloco title="Idiomas">
          <ul className="divide-y">
            {perfil.idiomas.map((idioma) => (
              <li key={idioma.nome} className="py-2 first:pt-0 last:pb-0">
                <p className="text-sm font-semibold">{idioma.nome}</p>
                {idioma.nivel && <p className="text-xs text-muted-foreground">{idioma.nivel}</p>}
              </li>
            ))}
          </ul>
        </Bloco>
      )}
    </div>
  );
}
